import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy } from 'react';

// ==============================|| LAYOUTS & COMPONENTS IMPORTS ||============================== //
import MinimalLayout from 'layout/MinimalLayout';
import Loadable from 'components/Loadable';
import { isAuthenticated } from 'utils/auth';

// ==============================|| AUTH GUARD ||============================== //
const AuthGuard = ({ children }) => {
  const authenticated = isAuthenticated();
  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

// ==============================|| LAZY LOADED PAGES ||============================== //
const Dashboard = Loadable(lazy(() => import('layout/Dashboard/index')));
const DashboardDefault = Loadable(lazy(() => import('pages/dashboard/index')));
const AuthLogin = Loadable(lazy(() => import('pages/authentication/login')));
const AuthRegister = Loadable(lazy(() => import('pages/authentication/register')));
const Members = Loadable(lazy(() => import('pages/members/membersComponent/members')));
const Department = Loadable(lazy(() => import('pages/members/department/department')));
const Designation = Loadable(lazy(() => import('pages/members/designation/designation')));
const Tasks = Loadable(lazy(() => import('pages/events/Tasks')));
const Events = Loadable(lazy(() => import('pages/events/Events')));
const TaskReport = Loadable(lazy(() => import('pages/reports/TaskReport')));
const RegistrationList = Loadable(lazy(() => import('pages/registration/RegistrationList')));
const RegistrationView = Loadable(lazy(() => import('pages/registration/RegistrationView')));
const RegistrationForm = Loadable(lazy(() => import('pages/events/registrationForm')));
const Participant = Loadable(lazy(() => import('pages/participants/participantComponent/participant')));
const ParticipantClass = Loadable(lazy(() => import('pages/participants/participantClassComponent/participantClass')));
const Organization = Loadable(lazy(() => import('pages/setup/organization')));
const TaskActivityForm = Loadable(lazy(() => import('pages/events/TaskActivityForm')));
const Zone = Loadable(lazy(() => import('pages/participants/zoneComponent/zone')));

// ==============================|| ROUTE DEFINITIONS ||============================== //

// LOGIN & REGISTRATION ROUTES
const LoginRoutes = {
  path: '/',
  element: <MinimalLayout />,
  children: [
    {
      path: '/',
      element: isAuthenticated() ? <Navigate to="/registration" replace /> : <AuthLogin />
    },
    {
      path: '/register',
      element: isAuthenticated() ? <Navigate to="/registration" replace /> : <AuthRegister />
    },
    {
      path: '/login',
      element: isAuthenticated() ? <Navigate to="/registration" replace /> : <AuthLogin />
    }
  ]
};

// PUBLIC ROUTES (e.g., registration form for external users)
const PublicRoutes = {
  path: '/',
  element: <MinimalLayout />,
  children: [
    {
      path: 'registerForm/:clientId/:eventId',
      element: <RegistrationForm />
    }
  ]
};

// MAIN (PROTECTED) ROUTES
const MainRoutes = {
  path: '/',
  element: <AuthGuard><Dashboard /></AuthGuard>,
  children: [
    { path: '/', element: <DashboardDefault /> },
    { path: '/dashboard', children: [ { path: 'default', element: <DashboardDefault /> } ] },
    { path: '/registration', element: <RegistrationList /> },
    { path: '/registration/view/:eventId/:registrantId', element: <RegistrationView /> },
    { path: '/members', element: <Members /> },
    { path: '/department', element: <Department /> },
    { path: '/designation', element: <Designation /> },
    { path: '/participants', element: <Participant /> },
    { path: '/participantClass', element: <ParticipantClass /> },
    { path: '/taskReports', element: <TaskReport /> },
    { path: '/task', element: <Tasks /> },
    { path: '/organization', element: <Organization /> },
    { path: '/events', element: <Events /> },
    { path: '/events/tasks', element: <Tasks /> },
    { path: '/events/tasks/activity/:id', element: <TaskActivityForm /> },
    { path: '/zone', element: <Zone /> }
  ]
};

// ==============================|| ROUTER CREATION ||============================== //

const router = createBrowserRouter(
  [MainRoutes, LoginRoutes, PublicRoutes],
  {
    basename: import.meta.env.VITE_APP_BASE_NAME,
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);

export default router;
